import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sprout, Lock, Mail, ChevronRight } from 'lucide-react';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    const { signIn, signInWithGoogle } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const { error } = await signIn(email, password);

        if (error) {
            setError(error.message);
            setLoading(false);
        } else {
            navigate('/', { replace: true });
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        setGoogleLoading(true);
        const { error } = await signInWithGoogle();
        if (error) {
            setError(error.message);
            setGoogleLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-dark-bg relative overflow-hidden">
            {/* Animated Background Gradients */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-accent-purple/10 blur-[120px] rounded-full animate-pulse-slow" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-accent-blue/10 blur-[120px] rounded-full animate-pulse-slow" style={{ animationDelay: '2s' }} />

            <div className="card max-w-sm w-full bg-dark-surface/40 backdrop-blur-xl border-dark-border/60 p-8 shadow-2xl relative">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-purple/20 to-accent-blue/20 rounded-3xl border border-white/5 mb-6 shadow-inner">
                        <Sprout size={32} className="text-accent-purple animate-pulse-slow" />
                    </div>
                    <h1 className="text-3xl font-black tracking-tight italic mb-2">
                        Duo<span className="text-accent-purple">Hub</span>
                    </h1>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-dark-muted px-4 py-1.5 bg-dark-bg/50 rounded-full border border-dark-border/50 inline-block">
                        Private Vault
                    </p>
                </div>

                <div className="space-y-6">
                    {error && (
                        <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-4 py-3 rounded-2xl text-[11px] font-bold animate-in slide-in-from-top-2">
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleGoogleSignIn}
                        disabled={googleLoading || loading}
                        className="w-full flex items-center justify-center gap-3 bg-white text-dark-bg h-14 rounded-2xl font-black text-sm hover:bg-white/90 transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
                    >
                        {googleLoading ? (
                            <div className="w-5 h-5 border-2 border-dark-bg/20 border-t-dark-bg rounded-full animate-spin" />
                        ) : (
                            <>
                                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                                Continue with Google
                            </>
                        )}
                    </button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-dark-border/50"></span>
                        </div>
                        <div className="relative flex justify-center text-[10px] uppercase font-black tracking-widest">
                            <span className="px-3 bg-[#0a0a0c] text-dark-muted">OR</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-dark-muted uppercase tracking-widest ml-2">Email</label>
                            <div className="relative group">
                                <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-muted group-focus-within:text-accent-purple transition-colors" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input h-12 pl-11 bg-dark-bg/50 border-dark-border/40 focus:border-accent-purple/50 text-sm font-bold"
                                    placeholder="you@creatives.com"
                                    required
                                    disabled={loading || googleLoading}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-dark-muted uppercase tracking-widest ml-2">Password</label>
                            <div className="relative group">
                                <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-muted group-focus-within:text-accent-purple transition-colors" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input h-12 pl-11 bg-dark-bg/50 border-dark-border/40 focus:border-accent-purple/50 text-sm font-bold"
                                    placeholder="••••••••"
                                    required
                                    disabled={loading || googleLoading}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || googleLoading}
                            className="btn-primary w-full h-14 flex items-center justify-center gap-2 font-black uppercase tracking-widest text-xs group"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    Sign In <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <div className="mt-10 pt-6 border-t border-dark-border/30 text-center">
                    <p className="text-[10px] font-bold text-dark-muted leading-relaxed uppercase tracking-widest opacity-60">
                        Private Access • By Invitation Only<br />
                        Vault Access Only
                    </p>
                </div>
            </div>
        </div>
    );
}
